import React, { ChangeEvent, useState } from "react";
import { useTranslation } from "react-i18next";

import { strengthLabel } from "@constants/login";
import { PasswordFieldProp } from "@models/components/formElements.model";
const PasswordLabel: React.FC<PasswordFieldProp> = ({
  autoComplete = "",
  className = "",
  isError,
  isIcon,
  labelText,
  onChange,
  onKeyDown,
  onStrengthChange, // New prop for strength callback
  showStrength = false,
  testId,
}) => {
  const [showPassword, setShowPassword] = useState(false);

  const [meter, setMeter] = React.useState(false);
  const [password, setPassword] = React.useState("");
  const { t } = useTranslation();
  const [progressWidth, setProgressWidth] = React.useState(10);
  const [strengthText, setStrengthText] = React.useState(
    strengthLabel.weak.label
  );
  const maxLength = 100;
  const [passwordStrength, setPasswordStrength] = React.useState(0);
  const [passwordTracker, setPasswordTracker] = React.useState({
    eightCharsOrGreater: 0,
    isMailLike: true,
    lowercase: false,
    number: false,
    specialChar: false,
    uppercase: false,
  });

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    try {
      const newPassword = e.target.value;
      setPassword(newPassword);
      onChange?.(e);
      const strength = calculatePasswordStrength(newPassword);
      setPasswordStrength(strength);
      onStrengthChange?.(strength);
      setPasswordTrackerChange(newPassword);
      progressBarRender(strength);
    } catch (error) {
      console.error("Error handling password input:", error);
    }
  };

  const setPasswordTrackerChange = (password: string) => {
    const atLeastOneUppercase = /[A-Z]/.test(password); // capital letters from A to Z
    const atLeastOneLowercase = /[a-z]/.test(password); // small letters from a to z
    const atLeastOneNumeric = /\d/.test(password); // numbers from 0 to 9
    const atLeastOneSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password); // any of the special characters within the square brackets
    const eightCharsOrMore = password.length; // eight characters or more
    const isMailLike = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/.test(
      password
    );

    setPasswordTracker({
      eightCharsOrGreater: eightCharsOrMore,
      isMailLike: !isMailLike,
      lowercase: atLeastOneLowercase,
      number: atLeastOneNumeric,
      specialChar: atLeastOneSpecialChar,
      uppercase: atLeastOneUppercase,
    });
  };

  const progressBarRender = (strength: number) => {
    if (strength === 9) {
      setStrengthText(strengthLabel.veryStrong.label);
      setProgressWidth(strengthLabel.veryStrong.width);
    } else if (strength === 8) {
      setStrengthText(strengthLabel.strong.label);
      setProgressWidth(strengthLabel.strong.width);
    } else if (strength === 7) {
      setStrengthText(strengthLabel.normal.label);
      setProgressWidth(strengthLabel.normal.width);
    } else {
      setStrengthText(strengthLabel.weak.label);
      setProgressWidth(strengthLabel.weak.width);
    }
  };

  const progressBarGradient = (passwordStrength: number) => {
    if (passwordStrength >= 9) {
      return "fg-bg-success";
    } else if (passwordStrength >= 8) {
      return "fg-bg-info";
    } else if (passwordStrength >= 7) {
      return "fg-bg-warning";
    } else {
      return "fg-bg-error";
    }
  };
  const calculatePasswordStrength = (password: string): number => {
    try {
      if (typeof password !== "string") return 0;

      let strength = 0;
      const atLeastOneUppercase = /[A-Z]/.test(password) ? 1 : 0;
      const atLeastOneLowercase = /[a-z]/.test(password) ? 1 : 0;
      const atLeastOneNumeric = /\d/.test(password) ? 1 : 0;
      const atLeastOneSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password)
        ? 1
        : 0;
      const eightCharsOrMore = password.length;
      const isMailLike = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/.test(
        password
      )
        ? 0
        : 1;

      const meetsMinimumCriteria =
        atLeastOneUppercase &&
        atLeastOneLowercase &&
        atLeastOneNumeric &&
        atLeastOneSpecialChar &&
        eightCharsOrMore >= 8 &&
        isMailLike;

      if (meetsMinimumCriteria) {
        strength = 6;
        if (eightCharsOrMore >= 25) strength += 3;
        else if (eightCharsOrMore >= 16) strength += 2;
        else if (eightCharsOrMore >= 8) strength += 1;
      }

      return strength;
    } catch (error) {
      console.error("Error handling password input:", error);
      return 7;
    }
  };

  return (
    <>
      <input
        type={showPassword ? "text" : "password"}
        value={password}
        data-testid={testId}
        className={`form-control${isError ? " border border-danger" : ""} ${className}`}
        onChange={handleChange}
        onKeyDown={onKeyDown}
        onFocus={() => setMeter(true)}
        maxLength={maxLength}
        {...(autoComplete ? { autoComplete } : {})}
      />
      <label className={isError ? "text-danger" : ""}>{labelText}</label>
      {isIcon && (
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="fg-cursor-pointer"
        >
          <i
            className={`fg-form-field-icon fa-solid ${showPassword ? "fa-eye-slash" : "fa-eye"}`}
          />
        </button>
      )}
      {showStrength && (
        <>
          {password && (
            <div className="progress fg-password-progress-bar-wrapper rounded mt-2 position-relative">
              <div
                className={`progress-bar rounded position-relative ${progressBarGradient(passwordStrength)}`}
                style={{
                  width: `${progressWidth}%`,
                }}
              >
                <span className="fg-password-progress-bar position-absolute">
                  {strengthText}
                </span>
              </div>
            </div>
          )}
          {password && meter && (
            <div className="fg-pasword-strength-msg-wrapper">
              <div className="text-danger">
                {passwordStrength <= 6 && t("passwordModal.mustContain")}
                <ul>
                  {passwordTracker.eightCharsOrGreater < 8 && (
                    <li>{t("passwordModal.eightCharsOrGreater")}</li>
                  )}
                  {!(
                    passwordTracker.uppercase && passwordTracker.lowercase
                  ) && <li>{t("passwordModal.upperlowercase")}</li>}
                  {!passwordTracker.specialChar && (
                    <li>{t("passwordModal.specialChar")}</li>
                  )}
                  {!passwordTracker.number && (
                    <li>{t("passwordModal.number")}</li>
                  )}
                  {!passwordTracker.isMailLike && (
                    <li>{t("passwordModal.isMailLike")}</li>
                  )}
                </ul>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default PasswordLabel;
